import { Stage } from 'application/stage'
import { useStore } from 'effector-react'
import { CitySelectForm } from 'features/city-select/components/city-select-form'
import {
  $cities,
  $cityWasJustAutoDetected,
  $fetching,
  $selectedCities,
  acceptAutoDetectedCity,
  fetchCitiesFx,
  getNearest,
  setSelectedCities
} from 'features/city-select/model'
import { createPluralFormWithPlaceholder } from 'lib/pluralize'
import { createMediaStyles } from 'lib/styled-helpers'
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'
import { Icon } from 'ui/icon'
import { ModalWindow } from 'ui/modal-window'

import { getProjectName } from '../../../lib/get-project-name'
import { Button } from '../../../ui/button'

export type CitySelectProps = {
  /**
   * URL апи для получения списка городов
   */
  getCitiesUrl: string

  i18n: {
    /**
     * Надпись для вывода когда выбраны все горда или не выбран ни один
     */
    allCities: string

    /**
     * Вопрос "правильно ли мы определили ваш город?"
     */
    isItYourCity: string

    /**
     * Текст на кнопке подтверждения авто-определённого города
     */
    yesItIsMyCity: string

    /**
     * Текст на кнопке отклонения авто-определённого города
     */
    noItIsNotMyCity: string

    /**
     * Текст заголовка окна, "выберите ваш город"
     */
    selectYourCity: string

    /**
     * Надпись на кнопке, раскрывающей полный список городов
     */
    showMoreCities: string

    /**
     * Надпись на кнопке подтверждения выбора городов
     */
    applyCitiesSelection: string

    /**
     * Плейсхолдер для инпута поиска городов
     */
    findYourCity: string

    selectedXCitiesForms: [string, string, string]

    noCitiesFound: string
  }

  /**
   * Включает определение по браузерной геолокации.
   */
  useGeolocation?: boolean

  selecting?: boolean
  setSelecting?: (val: boolean) => void
}

export const CitySelect: Stage<CitySelectProps> = ({ getCitiesUrl, i18n, useGeolocation, selecting, setSelecting }) => {
  const [confirmCity, setConfirmCity] = useState(false)
  const [whereAreYou, setWhereAreYou] = useState(false)
  const tooltipTargetRef = useRef<HTMLInputElement>(null)
  const tooltipTargetElement = tooltipTargetRef.current
  const fetching = useStore($fetching)
  const startSelecting = () => {
    setSelecting && setSelecting(true)
    ReactTooltip.hide()
  }

  const cityWasJustAutoDetected = useStore($cityWasJustAutoDetected)
  const endSelecting = () => setSelecting && setSelecting(false)

  /*TODO если разработка, то берем url из моков, если тест или прод, то составляем из частей , так как в моках не приходит весь url*/
  const url_from_nearest =
    process.env.NODE_ENV === 'development' ? getCitiesUrl : document.location.origin + getCitiesUrl
  const citiesUrl = new URL(url_from_nearest)

  useEffect(() => {
    fetchCitiesFx(getCitiesUrl).then(() => {
      setSelectedCities(selectedCities)
    })
  }, [getCitiesUrl])
  useLayoutEffect(() => {
    if (cityWasJustAutoDetected && tooltipTargetElement) {
      ReactTooltip.show(tooltipTargetElement)
    }
  }, [tooltipTargetElement, cityWasJustAutoDetected])

  const allCities = useStore($cities)
  const selectedCities = useStore($selectedCities)

  const onFormSubmit = (cities: string[]) => {
    setSelectedCities(cities)
    acceptAutoDetectedCity()
    endSelecting()
  }

  const setIconColor = () => {
    if (getProjectName() === 'generic_v2') return '#828282'
    return null
  }

  const CityConfirmation = () => (
    <StyledCityConfirmation>
      {i18n.isItYourCity} <Link onClick={() => acceptAutoDetectedCity()}>{i18n.yesItIsMyCity}</Link>
      <Link
        onClick={() => {
          if (useGeolocation) {
            setConfirmCity(true)
            setWhereAreYou(true)
          } else {
            /*Старое поведение*/
            startSelecting()
          }
        }}
      >
        {i18n.noItIsNotMyCity}
      </Link>
    </StyledCityConfirmation>
  )

  return (
    <>
      {allCities.length > 1 && getProjectName() !== 'avito' && (
        <CityWrapper
          ref={tooltipTargetRef}
          data-tip={'custom show'}
          data-for={'selectedCities'}
          onClick={startSelecting}
          data-event={cityWasJustAutoDetected}
          data-event-off={selectedCities}
        >
          <CityName disabled={fetching}>
            <SelectedCitiesLabel cities={allCities} selectedCities={selectedCities} i18n={i18n} />
          </CityName>

          <IconWrapper>
            <Icon
              name={getProjectName() === 'generic_v2' ? 'placeholder-generic-v2' : 'placeholder'}
              color={setIconColor()}
            />
          </IconWrapper>
        </CityWrapper>
      )}

      {cityWasJustAutoDetected &&
        !confirmCity &&
        !useGeolocation &&
        allCities.length > 1 &&
        getProjectName() !== 'haval' &&
        getProjectName() !== 'avito' && <CityConfirmationWrap>{CityConfirmation()}</CityConfirmationWrap>}
      {selecting &&
        createPortal(
          <ModalWindow
            visible={selecting}
            onCloseRequest={endSelecting}
            title={i18n.selectYourCity}
            sizeTextTitle={getProjectName() === 'haval' ? '2.4rem' : '2rem'}
          >
            <CitySelectForm
              cities={allCities}
              selectedCities={selectedCities}
              onSubmit={onFormSubmit}
              i18n={i18n}
              visible={selecting}
            />
          </ModalWindow>,
          document.body
        )}
      {createPortal(
        <ModalWindow
          visible={whereAreYou}
          onCloseRequest={() => setWhereAreYou(false)}
          title={'Текущее местоположение не определено'}
          positionTitle={useGeolocation ? 'left' : 'center'}
          sizeTextTitle={'22px'}
          paddingTop={'0'}
          paddingLeft={'2.4rem'}
          paddingRight={'2.4rem'}
          paddingBottom={'2.4rem'}
          useGeolocation={useGeolocation}
        >
          <Text>Разрешите определить ваше местоположение, чтобы найти ближайший дилерский центр</Text>
          <ButtonWrapper>
            <Button
              onClick={() => {
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    citiesUrl.searchParams.append('latitude', String(position.coords.latitude))
                    citiesUrl.searchParams.append('longitude', String(position.coords.longitude))
                    fetchCitiesFx(citiesUrl.href).then((response) => {
                      getNearest(response.nearest)
                    })
                    setWhereAreYou(false)
                  },
                  () => {
                    /*Юзер отклонил определение местоположения или произошла ошибка определения*/
                    setWhereAreYou(false)
                    startSelecting()
                  }
                )
              }}
            >
              Разрешить
            </Button>
            <Button
              text={true}
              onClick={() => {
                setWhereAreYou(false)
                startSelecting()
              }}
            >
              Не сейчас
            </Button>
          </ButtonWrapper>
        </ModalWindow>,
        document.body
      )}
    </>
  )
}

const SelectedCitiesLabel: FC<{ cities: string[]; selectedCities: string[]; i18n: CitySelectProps['i18n'] }> = ({
  cities,
  selectedCities,
  i18n
}) => {
  if (cities.length === selectedCities.length || selectedCities.length === 0) {
    return <StyledSpan>{i18n.allCities}</StyledSpan>
  }
  if (selectedCities.length > 2) {
    return <StyledSpan>{createPluralFormWithPlaceholder(i18n.selectedXCitiesForms, selectedCities.length)}</StyledSpan>
  }
  return <StyledSpan>{selectedCities.join(', ')}</StyledSpan>
}

const StyledSpan = styled.span`
  color: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '#D9D9D9'
    return null
  }};
`

const CityWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    cursor: pointer;
  }
`

const IconWrapper = styled.div`
  color: ${({ theme }) => {
    if (getProjectName() === 'ora') return theme.palette.black
    return theme.palette.brand.primary
  }};
`

const CityName = styled.div<{ disabled: boolean }>`
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'initial')};

  ${({ theme }) => theme.textStyles.p2};
  font-size: ${() => {
    return getProjectName() === 'haval' ? '1.4rem' : null
  }};
  font-weight: ${() => {
    return getProjectName() === 'haval' ? '500' : null
  }};
  color: ${({ theme }) => (getProjectName() === 'tank' ? theme.palette.white : theme.palette.grey[900])};
`

const StyledCityConfirmation = styled.div`
  cursor: default;
  ${({ theme }) => theme.textStyles.p2};
`

const Link = styled.span`
  cursor: pointer;
  outline: none;
  margin-left: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[800]};

  &:hover {
    text-decoration: none;
    border-bottom-width: 0;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
`

const Text = styled.div`
  font-size: 16px;
  margin-bottom: 27px;
  ${createMediaStyles(() => ({
    phone: 'padding-right: 0;',
    tablet: 'padding-right: 175px;'
  }))}
`

const CityConfirmationWrap = styled.div`
  position: absolute;
  top: -39px;
  left: 50%;
  margin-left: -132px;
  width: 264px;
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  box-shadow: 4px 4px 8px rgb(0 0 0 / 10%);
`
